<script setup lang="ts">
	import { useQueryClient } from '@tanstack/vue-query'
	import { ChevronDown, Filter, Mail, Plus, Search } from 'lucide-vue-next'
	import { computed, onUnmounted, provide, ref, watch } from 'vue'

	import BreadCrumb from '@/components/BreadCrumb.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import { Button } from '@/components/ui/button'
	import {
		DropdownMenu,
		DropdownMenuContent,
		DropdownMenuItem,
		DropdownMenuTrigger,
	} from '@/components/ui/dropdown-menu'
	import { Input } from '@/components/ui/input'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useCompaniesSearch from '@/modules/companies/composables/useCompaniesSearch'
	import useCompany from '@/modules/companies/composables/useCompany'
	import useCompanyDepartment from '@/modules/companies/composables/useCompanyDepartment'
	import { Company } from '@/modules/companies/interfaces/company'
	import { useCompanyGroups } from '@/modules/groups/composables/useCompanyGroups'
	import useRoles from '@/modules/roles/composables/useRoles'

	import SupervisorFilter from '../components/SupervisorFilter.vue'
	import UserFilters from '../components/UserFilters.vue'
	import UsersList from '../components/UsersList.vue'
	import useUsersCompany from '../composables/useUsersCompany'
	import useUserApprovals from '../composables/useUserApprovals'

	const filterCompanyId = ref<Company | undefined>()
	const openSliderFilter = ref(false)
	const usersListRef = ref<InstanceType<typeof UsersList>>()
	const { user } = useAuthUser()
	const queryClient = useQueryClient()
	const { hasPermissionUser } = useHasPermissionUser()
	const canCreateUsers = computed(() => hasPermissionUser('create-users', 'App\\Models\\Company'))
	const canViewApprovals = computed(() =>
		hasPermissionUser('view', 'App\\Models\\Approval') ||
		hasPermissionUser('viewAny', 'App\\Models\\Approval')
	)

	const { paginations } = useUserApprovals({
		immediate: true,
		params: { company: user.value?.companyId },
	})
	const pendingCount = computed(() => paginations.value?.pending?.meta?.total ?? 0)

	const {
		users,
		page,
		filters,
		pagination,
		isLoading,
		isFetching,
		isRefetching,
		setPage,
	} = useUsersCompany({}, { immediate: true })

	const safePagination = computed(() => {
		return (
			pagination.value || {
				links: [],
				meta: {
					currentPage: 0,
					from: 0,
					lastPage: 0,
					links: [],
					path: '',
					perPage: 0,
					to: 0,
					total: 0,
				},
			}
		)
	})

	const pages = [
		{ name: 'Usuarios', to: { name: 'users.index' }, current: true },
	]

	const { company } = useCompany(
		{
			companyId: filters.value.company,
		},
		{
			immediate: false,
		}
	)

	const { companies, setQuery: searchCompany } = useCompaniesSearch({
		immediate: true,
		queryKey: ['companiesSearchUser'],
		params: {
			corporate: user.value?.corporationId,
		},
	})

	const {
		data: roles,
		refetch: refetchRoles,
		setCompanyId,
		isLoading: loadingRoles,
	} = useRoles(
		{
			companyId: filters.value.company,
			immediate: true,
		},
		false
	)

	const {
		data: groups,
		setCompany: setCompanyGroups,
		refetch: refetchGroups,
		isLoading: loadingGroups,
	} = useCompanyGroups({
		immediate: true,
		params: {
			companyId: filters.value.company,
		},
	})

	const {
		departments,
		refetch: refetchDepartment,
		setCompanyId: setCompanyDep,
		isLoading: loadingDepartments,
	} = useCompanyDepartment({
		immediate: true,
		params: {
			companyId: filters.value.company,
		},
	})

	const invalidateQuery = async () => {
		await queryClient.invalidateQueries(['usersCompany'])
	}

	provide('usersData', { invalidateQuery })

	const handlePage = (newPage: string) => {
		const pageNumber = Number(newPage)

		if (
			pageNumber >= 1 &&
			pagination.value?.meta?.lastPage &&
			pageNumber <= pagination.value.meta.lastPage
		) {
			setPage(newPage)
		}
	}

	const toggleSliderFilter = () => {
		openSliderFilter.value = !openSliderFilter.value
	}

	watch(
		() => filters.value.company,
		async (newCompanyId) => {
			if (!newCompanyId) return

			setCompanyId(newCompanyId)
			refetchRoles()

			setCompanyGroups(newCompanyId.toString())
			refetchGroups()

			setCompanyDep(newCompanyId)
			refetchDepartment()

			handlePage('1')
		},
		{ flush: 'post' }
	)
</script>

<template>
	<div class="min-h-screen bg-slate-50/50">
		<BreadCrumb :pages="pages" />

		<main class="container mx-auto mt-4 px-4 pb-12">
			<div class="flex flex-col gap-6">
				<!-- Header Section -->
				<div class="bg-white rounded-xl border shadow-sm p-6">
					<div
						class="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6"
					>
						<div class="space-y-1">
							<h1 class="text-3xl font-bold tracking-tight text-foreground">
								Gestión de Usuarios
							</h1>
							<p class="text-muted-foreground">
								{{
									filters.company && company?.name ?
										`Usuarios de ${company.name}`
									: filters.company && filterCompanyId?.name ?
										`Usuarios de ${filterCompanyId.name}`
									:	'Administra y supervisa todos los usuarios de la plataforma'
								}}
							</p>
						</div>

						<div
							class="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto"
						>
							<div class="relative w-full sm:w-72">
								<Search
									class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
								/>
								<Input
									v-model="filters.search"
									@input="() => { filters.status = null; setPage('1') }"
									placeholder="Buscar por nombre o correo..."
									class="pl-9 bg-slate-50 border-slate-200 focus:bg-white transition-all"
								/>
							</div>

							<!-- Filtro Supervisado por (solo Master / Travel Manager) -->
							<SupervisorFilter class="w-full sm:w-auto" />

							<div class="flex items-center gap-2 w-full sm:w-auto">
								<Button
									variant="outline"
									@click="toggleSliderFilter"
									class="flex-1 sm:flex-none gap-2"
								>
									<Filter class="h-4 w-4" />
									Filtros
								</Button>

								<RouterLink v-if="canViewApprovals" :to="{ name: 'users.approvals' }" class="relative flex-1 sm:flex-none">
									<Button variant="outline" class="w-full gap-2">
										<span>Pendientes</span>
									</Button>
									<span
										v-if="pendingCount > 0"
										class="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold leading-none"
									>{{ pendingCount }}</span>
								</RouterLink>

								<!-- Crear: agrupado en un solo botón primario con menú -->
								<DropdownMenu v-if="canCreateUsers">
									<DropdownMenuTrigger as-child>
										<Button class="flex-1 sm:flex-none gap-2 shadow-sm">
											<Plus class="h-4 w-4" />
											<span>Nuevo</span>
											<ChevronDown class="h-4 w-4 opacity-80" />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" class="w-52">
										<DropdownMenuItem as-child>
											<RouterLink :to="{ name: 'users.create' }" class="gap-2">
												<Plus class="h-4 w-4" />
												Nuevo Usuario
											</RouterLink>
										</DropdownMenuItem>
										<DropdownMenuItem
											class="gap-2"
											@select="usersListRef?.actionModalInviteUser()"
										>
											<Mail class="h-4 w-4" />
											Invitar Usuario
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						</div>
					</div>

					<UserFilters
						:is-index="false"
						:companies="companies"
						:roles="roles || []"
						:groups="groups || []"
						:departments="departments || []"
						v-model:company="filterCompanyId"
						@searchCompany="searchCompany"
						:grid-class="'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'"
						:open-slide-over="openSliderFilter"
						@toggleFilter="toggleSliderFilter"
						:refetching-user="isRefetching"
						:loading-departments="loadingDepartments"
						:loading-groups="loadingGroups"
						:loading-roles="loadingRoles"
					/>
				</div>

				<!-- Table Section -->
				<div class="relative">
					<!-- Loading Overlay -->
					<transition
						enter-active-class="transition-all duration-300 ease-out"
						enter-from-class="opacity-0"
						enter-to-class="opacity-100"
						leave-active-class="transition-all duration-200 ease-in"
						leave-from-class="opacity-100"
						leave-to-class="opacity-0"
					>
						<div
							v-if="isFetching && !isLoading"
							class="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-20 flex items-center justify-center rounded-xl"
						>
							<div
								class="flex flex-col items-center gap-3 p-6 bg-white rounded-2xl shadow-xl border animate-in fade-in zoom-in duration-300"
							>
								<div
									class="h-8 w-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"
								/>
								<p class="text-sm font-semibold text-foreground">
									Actualizando...
								</p>
							</div>
						</div>
					</transition>

					<UsersList
						ref="usersListRef"
						:is-tab="true"
						:users="users"
						:page="page"
						:pagination="safePagination"
						@update:changePage="handlePage"
						@refetch="invalidateQuery"
						:permission="'create-users'"
						:model="'App\\Models\\Company'"
						:to-show-user="{ name: 'users.show' }"
						:to-edit-user="{ name: 'users.edit' }"
						:to-permissions-user="{ name: 'users.permissions' }"
					/>
				</div>
			</div>
		</main>

		<LoadingModal :when="isLoading" />
	</div>
</template>

<style scoped>
	.list-enter-active,
	.list-leave-active {
		transition: all 1s ease;
	}

	.list-enter-from,
	.list-leave-to {
		opacity: 0;
		transform: translateX(30px);
	}

	/* Hide scrollbar for Chrome, Safari and Opera */
	.table::-webkit-scrollbar {
		display: none;
	}

	/* Hide scrollbar for IE, Edge and Firefox */
	.table {
		-ms-overflow-style: none;
		/* IE and Edge */
		scrollbar-width: none;
		/* Firefox */
	}

	.active-user {
		position: relative;
	}

	.active-user::before {
		content: '';
		width: 20px;
		height: 20px;
		position: absolute;
		background-color: #10b981;
		border-radius: 15px;
		right: 0;
		top: 0;
		z-index: 10;
	}

	.inactive-user {
		position: relative;
	}

	.inactive-user::before {
		content: '';
		width: 20px;
		height: 20px;
		position: absolute;
		background-color: #f87171;
		border-radius: 15px;
		right: 10px;
		top: 10px;
		z-index: 10;
	}
</style>
